import { $t } from '@/i18n';
import wujiFetch from '@/utils/wujiFetch';
import { message } from '@utils/wuji-message';
import { cloneDeep } from 'lodash';
import { isRuntime } from '@/utils/globalInfo';
import { getComponentHubStoreInfo, loadComponentHubStores } from '@/utils/componentHubStore';
const prefix = '/api/xy/template/component';
function getUrl(path) {
    return `${prefix}${path}`;
}
export var CompTemplateStateMode;
(function (CompTemplateStateMode) {
    CompTemplateStateMode["create"] = "create";
    CompTemplateStateMode["replace"] = "replace";
    CompTemplateStateMode["list"] = "list";
})(CompTemplateStateMode || (CompTemplateStateMode = {}));
export default {
    state: {
        mode: 'list',
        selectComponent: null,
        templateList: [],
        editableTemplates: [],
        updatableTemplates: [],
        selectComponentInfo: null,
    },
    actions: {
        /**
         * 获取组件模版列表
         */
        async fetchComponentTemplateList({ commit }, { projectId }) {
            try {
                const res = await wujiFetch(getUrl(`/list?exclude=pageInfo&projectid=${projectId}`), {
                    method: 'get',
                });
                commit('setCompTemplateState', {
                    templateList: res,
                });
                return res;
            }
            catch (err) {
                message.error($t('k_e901db9d', { arg_0: err.message }));
            }
        },
        /**
         * 获取组件模版列表
         */
        async getComponentTemplate({}, { id }) {
            try {
                const res = await wujiFetch(getUrl(`/get/${id}`), {
                    method: 'GET',
                });
                return res;
            }
            catch (err) {
                message.error($t('k_e4c77d2d'));
            }
        },
        /**
         * 更新模版
         */
        async updateComponentTemplate({ commit, dispatch }, { template }) {
            try {
                await wujiFetch(getUrl('/patch'), {
                    method: 'POST',
                    body: JSON.stringify(template),
                });
                message.success($t('k_853fb3ad'));
                commit('setCompTemplateState', {
                    mode: 'list',
                });
                dispatch('fetchComponentTemplateList', { projectId: template.projectId });
                dispatch('getEditableCompTemplates');
            }
            catch (error) {
                console.error(error);
                message.error(error.message);
            }
        },
        /**
         * 创建模版
         */
        async createComponentTemplate({ dispatch }, template) {
            try {
                const res = await wujiFetch(getUrl('/create'), {
                    method: 'post',
                    body: JSON.stringify(template),
                });
                dispatch('fetchComponentTemplateList', { projectId: template.projectId });
                message.success($t('k_b59111d3'));
                return res;
            }
            catch (error) {
                message.error(error.message);
            }
        },
        /**
         * 获取当前用户有编辑全局的模板list
         */
        async getEditableCompTemplates({ commit, rootState }, { type = '', projectId } = {}) {
            try {
                const url = getUrl(`/editable?projectid=${projectId || rootState.project.projectInfo?.id}&${type ? `filter=${encodeURIComponent(`type="${type}"`)}` : ''}`);
                const res = await wujiFetch(url, {
                    method: 'GET',
                });
                commit('setCompTemplateState', {
                    editableTemplates: res,
                });
                return res;
            }
            catch (err) {
                message.error($t('k_f5bb2eff'));
            }
        },
        /**
         * 删除模版
         */
        async deleteCompTemplate({ dispatch, rootState }, { _id, projectId }) {
            try {
                await wujiFetch(getUrl('/patch'), {
                    method: 'POST',
                    body: JSON.stringify({
                        _id,
                        disabled: 1,
                    }),
                });
                message.success($t('k_979fce5f'));
                dispatch('fetchComponentTemplateList', { projectId: projectId || rootState.project.projectInfo?.id });
                dispatch('getEditableCompTemplates');
            }
            catch (error) {
                console.error(error);
                message.error($t('k_912c9ac0'));
            }
        },
        /**
         * 模版排序
         */
        async sortCompTemplate({ dispatch, rootState }, { tplSort, projectId }) {
            try {
                const validProjectId = projectId || rootState.project.projectInfo?.id;
                await wujiFetch(getUrl(`/sort/create?projectid=${validProjectId}`), {
                    method: 'post',
                    body: JSON.stringify({ tplSort }),
                });
                dispatch('fetchComponentTemplateList', { projectId: validProjectId });
            }
            catch (err) {
                throw err;
            }
        },
        /**
         * 获取可更新的组件模板列表（用于 uc-designer 的 templates 参数）
         * 返回格式: [{ templateId: string, layout: object }]
         */
        async fetchUpdatableTemplates({ commit }, { projectId }) {
            // 运行时直出已注入则直接复用，避免重复 HTTP 请求
            const injected = typeof window !== 'undefined' ? window.xy_runtime_updatable_templates : undefined;
            if (Array.isArray(injected)) {
                const templates = injected
                    .map(item => ({
                    templateId: item.templateId,
                    version: item.version,
                    group: item.group,
                    layout: item.layout ? cloneDeep(item.layout) : null,
                    lessCode: item.lessCode,
                    stateSchema: item.stateSchema ? cloneDeep(item.stateSchema) : undefined,
                    querySchema: item.querySchema ? cloneDeep(item.querySchema) : undefined,
                    xySources: item.xySources ? cloneDeep(item.xySources) : undefined,
                    name: item.name,
                    icon: item.icon,
                    category: item.category,
                    componentStore: getComponentHubStoreInfo('local', item.group || ''),
                }))
                    .filter(t => t.layout);
                commit('setCompTemplateState', { updatableTemplates: templates });
                return templates;
            }
            // 运行时只信任直出注入的数据，未注入时不发请求，直接置空
            if (isRuntime()) {
                commit('setCompTemplateState', { updatableTemplates: [] });
                return [];
            }
            try {
                const [res] = await Promise.all([
                    wujiFetch(getUrl(`/updatable?projectid=${projectId}&projectEnv=dev&fullInfo=1&nocache=true`), {
                        method: 'GET',
                    }),
                    // 商店展示名不在模板信息里，组装物料前先加载一次
                    loadComponentHubStores(projectId),
                ]);
                // 将返回数据转换为 { templateId, layout } 格式
                const templates = (res || []).map((item) => {
                    const layout = item.componentInfo?.layout;
                    const stateSchema = item.componentInfo?.stateSchema;
                    const querySchema = item.componentInfo?.querySchema;
                    // 模板编辑器就是个 pagelet 编辑器，模板里建的数据源原样落在 componentInfo.xySources；
                    // 渲染期注入进页面内存，不落盘
                    const xySources = item.componentInfo?.xySources;
                    return {
                        templateId: item.templateId || item._id,
                        version: item.version,
                        group: item.group,
                        layout: layout ? cloneDeep(layout) : null,
                        lessCode: item.componentInfo?.lessCode,
                        stateSchema: stateSchema ? cloneDeep(stateSchema) : undefined,
                        querySchema: querySchema ? cloneDeep(querySchema) : undefined,
                        xySources: xySources ? cloneDeep(xySources) : undefined,
                        name: item.name,
                        icon: item.icon,
                        category: item.category,
                        componentStore: getComponentHubStoreInfo('local', item.group || ''),
                    };
                }).filter((t) => t.layout);
                commit('setCompTemplateState', { updatableTemplates: templates });
                return templates;
            }
            catch (err) {
                console.error('[fetchUpdatableTemplates]', err);
                return [];
            }
        },
    },
    mutations: {
        setCompTemplateState(state, payload) {
            Object.assign(state, payload);
        },
    },
};
